'use client'
import {
  ALGOLIA_SEARCH_CHANNEL,
  algoliaGetTokenFromLocalStorage,
  algoliaRecordAccessorGetDeeplink,
  type AlgoliaUserDataRedirect,
} from '../../../algolia'
import { type PublicGlobalConfig } from '../../../global-config'
import {
  urlNormalizeUri,
  urlResolverGetSearchPage,
} from '../../../url-handling'

import { type HeaderSearchContextValue } from './components/types'
import { previousSearchesStorageAddPreviousSearches } from './previousSearchesStorage'

const ALGOLIA_REDIRECT_TYPE = 'redirect'

const navigateToProductDetailPage = (deeplink: string): void => {
  globalThis.location.href = deeplink
}
const navigateToSearchResults = ({
  config,
  query,
}: {
  config: PublicGlobalConfig
  query: string
}): void => {
  globalThis.location.href = urlResolverGetSearchPage(config, {
    queryParams: {
      eventName: 'search-submit',
      // eslint-disable-next-line id-length -- "i" represents that the search is internal, e.g. originates from site itself
      i: '1',
      // eslint-disable-next-line id-length -- "q" represents the search query
      q: query,
      searchChannel: ALGOLIA_SEARCH_CHANNEL,
      userToken: algoliaGetTokenFromLocalStorage(),
    },
  })
}

const findFirstAlgoliaUserDataRedirect = (
  algoliaUserDataRedirects: AlgoliaUserDataRedirect[],
): AlgoliaUserDataRedirect | undefined => {
  return algoliaUserDataRedirects.find(
    (record) => record.type === ALGOLIA_REDIRECT_TYPE,
  )
}

const handleAlgoliaUserDataRedirectIfNeeded = (
  algoliaUserDataRedirects: AlgoliaUserDataRedirect[],
): boolean => {
  const firstRedirectRecord = findFirstAlgoliaUserDataRedirect(
    algoliaUserDataRedirects,
  )
  if (firstRedirectRecord) {
    const { redirectTarget } = firstRedirectRecord
    const path = urlNormalizeUri({ uri: redirectTarget })

    globalThis.location.href = path

    return true
  }

  return false
}

const handleProductDetailIfNeeded = (
  renderedSearchRecords: HeaderSearchContextValue['inputControls']['renderedSearchRecords'],
  searchInputValue: string,
): boolean => {
  if (renderedSearchRecords?.length === 1) {
    const [algoliaRecord] = renderedSearchRecords
    const { __position: position, __queryID: queryID } = algoliaRecord
    const deeplink = algoliaRecordAccessorGetDeeplink({
      params: {
        position,
        query: searchInputValue,
        queryID: queryID ?? '',
      },
      record: algoliaRecord,
    })

    if (deeplink) {
      navigateToProductDetailPage(deeplink)

      return true
    }
  }

  return false
}

/**
 * determines where to navigate when user submits search from header
 * 1. if there is a user data redirect of type "redirect" in algolia response, navigate there
 * 2. if there is exactly one search result, navigate to PDP of that result
 * 3. otherwise navigate to search results page
 */
export const headerSearchContextHandleSubmitSearch = (
  config: PublicGlobalConfig,
  inputControls: HeaderSearchContextValue['inputControls'],
): void => {
  const { algoliaUserDataRedirects, renderedSearchRecords, searchInputValue } =
    inputControls
  if (!searchInputValue) {
    return
  }
  previousSearchesStorageAddPreviousSearches(searchInputValue)

  if (handleAlgoliaUserDataRedirectIfNeeded(algoliaUserDataRedirects)) {
    return
  }

  if (handleProductDetailIfNeeded(renderedSearchRecords, searchInputValue)) {
    return
  }

  navigateToSearchResults({ config, query: searchInputValue })
}
